import { deriveLineAmount, hourlyBasisFromUnitPrice, type OfferPhase } from "@/lib/offer-calc";
import { type OfferLine, type OfferLineTemplate } from "@/hooks/use-offers";

export interface OfferDraft {
  title: string;
  description: string;
  overhead_pct: number;
  discount_pct: number;
  discount_amount: number;
  /** Per-tilbud override; null betyr arv fra system_config (VIB-555) */
  hourly_rate: number | null;
}

export interface OfferTotalsResult {
  subtotal: number;
  overhead_amount: number;
  after_overhead: number;
  discount_amount: number;
  total: number;
  perPhase: Record<OfferPhase, number>;
  internalHours: number;
  internalRevenue: number;
  externalCost: number;
  externalRevenue: number;
  bf: number;
  estHourlyRate: number | null;
}

export const EMPTY_PER_PHASE: Record<OfferPhase, number> = {
  pl: 0,
  preprod: 0,
  production: 0,
  postprod: 0,
  stills: 0,
  reise: 0,
};

export const EMPTY_OFFER_TOTALS: OfferTotalsResult = {
  subtotal: 0,
  overhead_amount: 0,
  after_overhead: 0,
  discount_amount: 0,
  total: 0,
  perPhase: EMPTY_PER_PHASE,
  internalHours: 0,
  internalRevenue: 0,
  externalCost: 0,
  externalRevenue: 0,
  bf: 0,
  estHourlyRate: null,
};

/**
 * Beregner neste sort_order for en gitt fase+section. Brukes når en ny linje
 * legges til på enden. Pure function — testbar uten hook-context.
 */
export function nextSortOrderFor(
  lines: OfferLine[],
  phase: OfferPhase,
  sectionId: string | null,
): number {
  const existing = lines.filter(
    (l) => l.phase === phase && (l.section_id ?? null) === sectionId,
  );
  if (existing.length === 0) return 10;
  return Math.max(...existing.map((l) => l.sort_order)) + 10;
}

/**
 * Beregner ny sort_order ved reorder via drag-drop. Settes som midtpunkt
 * mellom forrige nabo og mål, eller target - 5 hvis det ikke finnes noen før.
 * Returnerer også ny phase + section_id (drag-drop overskriver basert på mål).
 */
export function calcReorderSortOrder(
  lines: OfferLine[],
  sourceId: string,
  targetId: string,
): { sortOrder: number; phase: OfferPhase; sectionId: string | null } | null {
  const source = lines.find((l) => l.id === sourceId);
  const target = lines.find((l) => l.id === targetId);
  if (!source || !target) return null;

  // Drag-drop overskriver både fase OG section_id (VIB-663e) basert på mål-linja
  // — så samme handle flytter linjer mellom faser og mellom tilvalg/hoved.
  const newPhase = target.phase;
  const newSectionId = target.section_id ?? null;
  const phaseLines = lines
    .filter(
      (l) =>
        l.phase === newPhase &&
        (l.section_id ?? null) === newSectionId &&
        l.id !== source.id,
    )
    .sort((a, b) => a.sort_order - b.sort_order);
  const targetIdx = phaseLines.findIndex((l) => l.id === targetId);

  const before = phaseLines[targetIdx - 1];
  const newSortOrder = before
    ? (before.sort_order + target.sort_order) / 2
    : target.sort_order - 5;

  return {
    sortOrder: newSortOrder,
    phase: newPhase,
    sectionId: newSectionId,
  };
}

/**
 * Plasser en kopi rett etter kildelinjen (⌘D / Dupliser i raden).
 */
export function calcDuplicateAfterSortOrder(
  lines: OfferLine[],
  sourceId: string,
): { sortOrder: number; phase: OfferPhase; sectionId: string | null } | null {
  const source = lines.find((l) => l.id === sourceId);
  if (!source) return null;
  const sectionId = source.section_id ?? null;
  const peers = lines
    .filter(
      (l) =>
        l.phase === source.phase && (l.section_id ?? null) === sectionId,
    )
    .sort((a, b) => a.sort_order - b.sort_order);
  const idx = peers.findIndex((l) => l.id === sourceId);
  if (idx < 0) return null;
  const after = peers[idx + 1];
  return {
    sortOrder: after
      ? (source.sort_order + after.sort_order) / 2
      : source.sort_order + 10,
    phase: source.phase,
    sectionId,
  };
}

/**
 * Plasser en kopi foran målraden (Option-dra). Kilden blir stående, så
 * den telles med som nabo (ulikt calcReorderSortOrder).
 */
export function calcDuplicateAtTargetSortOrder(
  lines: OfferLine[],
  targetId: string,
): { sortOrder: number; phase: OfferPhase; sectionId: string | null } | null {
  const target = lines.find((l) => l.id === targetId);
  if (!target) return null;
  const sectionId = target.section_id ?? null;
  const peers = lines
    .filter(
      (l) =>
        l.phase === target.phase && (l.section_id ?? null) === sectionId,
    )
    .sort((a, b) => a.sort_order - b.sort_order);
  const targetIdx = peers.findIndex((l) => l.id === targetId);
  const before = peers[targetIdx - 1];
  return {
    sortOrder: before
      ? (before.sort_order + target.sort_order) / 2
      : target.sort_order - 5,
    phase: target.phase,
    sectionId,
  };
}

/** Felter for en duplisert offer-linje (ny id tildeles av insert). */
export function duplicateOfferLineFields(
  source: OfferLine,
  placement: { sortOrder: number; phase: OfferPhase; sectionId: string | null },
) {
  return {
    offer_id: source.offer_id,
    template_id: source.template_id ?? null,
    phase: placement.phase,
    description: source.description,
    cost_type: source.cost_type,
    pricing_type: source.pricing_type,
    quantity: source.quantity,
    unit: source.unit,
    unit_price: source.unit_price,
    purchase_price: source.purchase_price,
    amount: source.amount,
    comment: source.comment ?? null,
    sort_order: placement.sortOrder,
    section_id: placement.sectionId,
    overtime_split: source.overtime_split ?? null,
    rate_basis_hourly: source.rate_basis_hourly ?? null,
    is_auto_generated: false,
    auto_kind: null,
  };
}

/**
 * Bygger en CalcLine-array fra offer_lines for calcOfferTotals.
 * Eksportert for å gjenbruke i tester / hooks som beregner samme totals.
 */
export function toCalcLines(lines: OfferLine[]) {
  return lines.map((l) => ({
    phase: l.phase,
    cost_type: l.cost_type,
    pricing_type: l.pricing_type,
    quantity: l.quantity,
    unit: l.unit,
    unit_price: l.unit_price,
    purchase_price: l.purchase_price,
    amount: l.amount,
    overtime_split: l.overtime_split ?? null,
  }));
}

/**
 * Feltene en ny linje fra katalogen får (enhet, sats, beløp, tidsbasis).
 * Felles for katalogvalget i budsjettet og «Lag budsjettlinje» på et Idé-kort.
 * VIB-701: katalogen er eneste sannhet; sats faller tilbake til 0 når
 * default_rate er NULL, så feilkonfigurasjon synes.
 */
export function templateLineFields(template: OfferLineTemplate, qty: number | null, hourlyRate: number) {
  const isInternalHourly = template.cost_type === "internal" && template.pricing_type === "hourly";
  const unit = isInternalHourly ? template.default_unit || "t" : "stk";
  const unit_price = template.default_rate ?? 0;
  const quantity = qty ?? 0;
  const amount = deriveLineAmount(
    { cost_type: template.cost_type, pricing_type: template.pricing_type, quantity, unit, unit_price },
    hourlyRate,
  );
  return {
    template_id: template.id,
    phase: template.phase,
    description: template.name,
    cost_type: template.cost_type,
    pricing_type: template.pricing_type,
    quantity,
    unit,
    unit_price,
    amount,
    // VIB-2097: katalogsatsen er tidsbasisen for en t/d-linje.
    rate_basis_hourly: hourlyBasisFromUnitPrice(unit, unit_price),
  };
}
